import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /**
   * @type {number}
   */
  #appliedSubmenuHeight = 0;

  /**
   * @type {WeakMap<HTMLElement, number>}
   */
  #submenuHeightCache = new WeakMap();

  /**
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #closeTimer;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(() => {
      this.#preloadImages();
      const cacheHeights = () => this.#cacheShopSubmenuHeights();
      if (document.fonts?.ready) {
        document.fonts.ready.then(cacheHeights);
      } else {
        cacheHeights();
      }
    });
    window.addEventListener('resize', this.#resizeListener);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
    this.addEventListener('pointerleave', this.#onPointerLeave);
    this.addEventListener('pointerenter', this.#cancelClose);
    this.headerComponent?.addEventListener('pointerleave', this.#onHeaderPointerLeave);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    this.removeEventListener('pointerleave', this.#onPointerLeave);
    this.removeEventListener('pointerenter', this.#cancelClose);
    this.headerComponent?.removeEventListener('pointerleave', this.#onHeaderPointerLeave);
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.#cancelClose();
    this.#cleanupSubmenuObservers();
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
    this.#submenuHeightCache = new WeakMap();
    this.#cacheShopSubmenuHeights();
  }, 100);

  #overflowSubmenuListener = () => {
    this.#scheduleClose();
  };

  /**
   * Keep the mega menu open while moving from a top-level link into the panel.
   * The open underlay sits outside header-menu, so an immediate close would
   * fire before the pointer can reach sublinks.
   * @param {PointerEvent} event
   */
  #onPointerLeave = (event) => {
    if (this.#isStillInsideMenu(event.relatedTarget)) return;
    this.#scheduleClose();
  };

  /**
   * @param {PointerEvent} event
   */
  #onHeaderPointerLeave = (event) => {
    if (this.#isStillInsideMenu(event.relatedTarget)) return;
    this.#scheduleClose();
  };

  /**
   * @param {EventTarget | null} target
   * @returns {boolean}
   */
  #isStillInsideMenu(target) {
    if (target instanceof Node && this.contains(target)) return true;
    if (target instanceof Node && this.headerComponent?.contains(target)) return true;

    const submenu = this.querySelector('.menu-list__submenu[data-active]');
    if (submenu instanceof HTMLElement && submenu.matches(':hover')) return true;
    if (this.matches(':hover')) return true;
    if (this.headerComponent?.matches(':hover')) return true;

    return false;
  }

  #cancelClose = () => {
    clearTimeout(this.#closeTimer);
    this.#closeTimer = undefined;
  };

  #scheduleClose = () => {
    this.#cancelClose();
    this.#closeTimer = setTimeout(() => {
      this.#closeTimer = undefined;
      if (this.#isStillInsideMenu(null)) return;
      this.#forceClose();
    }, 180);
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Close any open mega menu (used by direct nav links without submenus).
   */
  closeSubmenu = () => {
    this.#forceClose();
  };

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.#cancelClose();
    this.dispatchEvent(new MegaMenuHoverEvent());

    if (!(event.target instanceof Element) || !this.headerComponent) return;

    const listItem = event.target.closest('.menu-list__list-item');
    if (listItem?.classList.contains('menu-list__list-item--direct-link')) {
      this.#forceClose();
      return;
    }

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    let submenu = findSubmenu(item);
    let hasSubmenu = Boolean(submenu);

    if (!hasSubmenu) {
      if (!isDefaultSlot) {
        submenu = this.overflowMenu;
        hasSubmenu = Boolean(submenu);
      } else {
        this.#forceClose();
        return;
      }
    }

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
      const previousSubmenu = findSubmenu(previouslyActiveItem);
      if (previousSubmenu) {
        delete previousSubmenu.dataset.active;
      }
    }

    this.#state.activeItem = item;

    if (submenu) {
      submenu.dataset.active = '';
      this.#cleanupSubmenuObservers();

      // Measure while submenu is still hidden, then reveal at the correct height (prevents blink).
      this.#applySubmenuHeight(submenu, isDefaultSlot, hasSubmenu, true);

      // Bottom pills / promo image can finish layout after the first paint.
      requestAnimationFrame(() => {
        if (this.#state.activeItem !== item) return;
        this.#applySubmenuHeight(submenu, isDefaultSlot, hasSubmenu, true);
        requestAnimationFrame(() => {
          if (this.#state.activeItem !== item) return;
          this.#applySubmenuHeight(submenu, isDefaultSlot, hasSubmenu, true);
        });
      });

      submenu.querySelectorAll('img').forEach((image) => {
        if (!(image instanceof HTMLImageElement) || image.complete) return;
        image.addEventListener(
          'load',
          () => {
            if (this.#state.activeItem !== item) return;
            this.#applySubmenuHeight(submenu, isDefaultSlot, hasSubmenu, true);
          },
          {once: true}
        );
      });
    } else {
      this.#applySubmenuHeight(null, isDefaultSlot, hasSubmenu);
    }

    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';
    this.style.setProperty('--submenu-opacity', '1');
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const menu = findSubmenu(this.#state.activeItem);
    const isMovingIntoSubmenu =
      event.relatedTarget instanceof Node && menu?.contains(event.relatedTarget);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    if (isMovingIntoSubmenu || isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) return;

    if (this.#isStillInsideMenu(event.relatedTarget)) return;

    const isMovingToAnotherMenuItem =
      event.relatedTarget instanceof Node &&
      event.relatedTarget.closest('.menu-list__list-item') instanceof HTMLElement;

    if (isMovingToAnotherMenuItem) {
      const targetItem = event.relatedTarget.closest('.menu-list__list-item');
      if (targetItem?.classList.contains('menu-list__list-item--direct-link')) {
        this.#forceClose();
      }
      return;
    }

    this.#scheduleClose();
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;

    if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) return;

    const submenu = findSubmenu(item);
    if (submenu?.matches(':hover')) return;
    if (this.#isStillInsideMenu(null)) return;

    this.#forceClose();
  };

  /**
   * Close the open submenu unconditionally.
   */
  #forceClose() {
    this.#cancelClose();
    const item = this.#state.activeItem;
    if (!item) {
      this.#appliedSubmenuHeight = 0;
      this.headerComponent?.style.setProperty('--submenu-height', '0px');
      this.#setFullOpenHeaderHeight(0);
      this.style.setProperty('--submenu-opacity', '0');
      this.dataset.overflowExpanded = 'false';
      this.ariaExpanded = 'false';
      return;
    }

    const submenu = findSubmenu(item);

    this.#cleanupSubmenuObservers();
    this.#appliedSubmenuHeight = 0;
    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    if (submenu) {
      delete submenu.dataset.active;
    }
  }

  /**
   * Measure submenu content height, including Shop bottom links after layout.
   * @param {HTMLElement} submenu
   * @returns {number}
   */
  #measureSubmenuContentHeight(submenu) {
    submenu.style.contentVisibility = 'visible';
    submenu.style.visibility = 'visible';

    const submenuInner = submenu.querySelector('.menu-list__submenu-inner');
    if (!(submenuInner instanceof HTMLElement)) {
      submenu.style.removeProperty('visibility');
      submenu.style.removeProperty('content-visibility');
      return submenu.offsetHeight || 0;
    }

    void submenuInner.offsetHeight;

    const {paddingTop, paddingBottom} = getComputedStyle(submenuInner);
    const padding = (parseFloat(paddingTop) || 0) + (parseFloat(paddingBottom) || 0);

    const contentMain = submenu.querySelector('.mega-menu-custom__main');
    const bottomLinks = submenu.querySelector('.mega-menu-custom__bottom-links');
    const innerRect = submenuInner.getBoundingClientRect();

    let height = Math.max(submenuInner.scrollHeight, submenuInner.offsetHeight);

    if (contentMain instanceof HTMLElement) {
      void contentMain.offsetHeight;
      height = Math.max(
        height,
        contentMain.scrollHeight + padding,
        contentMain.offsetHeight + padding
      );
    }

    if (bottomLinks instanceof HTMLElement && innerRect.height > 0) {
      const linksBottom = bottomLinks.getBoundingClientRect().bottom;
      height = Math.max(height, Math.ceil(linksBottom - innerRect.top + (parseFloat(paddingBottom) || 0)));
    }

    submenu.style.removeProperty('visibility');
    submenu.style.removeProperty('content-visibility');

    return Math.ceil(height);
  }

  /**
   * Pre-measure Shop submenu height so the first hover uses the correct panel size.
   */
  #cacheShopSubmenuHeights() {
    this.querySelectorAll('.menu-list__submenu').forEach((submenu) => {
      if (!(submenu instanceof HTMLElement)) return;
      if (!submenu.querySelector('.mega-menu-custom__bottom-links')) return;

      const hadActive = submenu.hasAttribute('data-active');
      submenu.dataset.active = '';
      const height = this.#measureSubmenuContentHeight(submenu);
      this.#submenuHeightCache.set(submenu, height);
      if (!hadActive) {
        delete submenu.dataset.active;
      } else {
        this.#applySubmenuHeight(submenu, true, true, true);
      }
    });
  }

  /**
   * Measure submenu height and sync --submenu-height + --full-open-header-height.
   * @param {HTMLElement | null | undefined} submenu
   * @param {boolean} isDefaultSlot
   * @param {boolean} hasSubmenu
   * @param {boolean} [forceLive]
   */
  #applySubmenuHeight(submenu, isDefaultSlot, hasSubmenu, forceLive = false) {
    if (!this.headerComponent) return;

    let finalHeight = 0;

    if (submenu) {
      const liveHeight = this.#measureSubmenuContentHeight(submenu);
      const cachedHeight = this.#submenuHeightCache.get(submenu) ?? 0;
      finalHeight = forceLive ? Math.max(liveHeight, cachedHeight) : cachedHeight || liveHeight;

      if (!isDefaultSlot) {
        const overflowListHeight = this.#getOverflowListLinksHeight();
        if (hasSubmenu) {
          const overflowHeight = this.overflowMenu?.offsetHeight || 0;
          finalHeight = Math.max(overflowHeight, overflowListHeight);
        } else {
          finalHeight = overflowListHeight;
        }
      }
    }

    if (Math.abs(finalHeight - this.#appliedSubmenuHeight) < 2) return;

    this.#appliedSubmenuHeight = finalHeight;
    if (submenu instanceof HTMLElement) {
      this.#submenuHeightCache.set(submenu, finalHeight);
    }
    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight);
  }

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    }

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   */
  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + (headerVisibleHeight ?? 0);

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupSubmenuObservers() {
    this.#cleanupMutationObserver();
  }

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  const fromListItem = element.closest('.menu-list__list-item')?.querySelector('[ref="menuitem"]');
  if (fromListItem instanceof HTMLElement) return fromListItem;

  const self = element.closest('[ref="menuitem"]');
  return self instanceof HTMLElement ? self : null;
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
